import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  dismissInvoiceMailCandidate,
  einvoiceLineCountFromCandidate,
  getInvoiceMailScanJob,
  INVOICE_MAIL_SCAN_JOB_STORAGE_KEY,
  listInvoiceMailCandidates,
  reanalyzeInvoiceMailCandidate,
  scanInvoiceMailbox,
  type InvoiceMailCandidate,
  type InvoiceMailScanJob,
  type InvoiceMailStatus,
} from "@/lib/invoiceMail";
import { ConfirmInvoiceMailDialog } from "@/pages/invoiceMail/ConfirmInvoiceMailDialog";
import { AssignInvoiceControl } from "@/pages/invoiceMail/AssignInvoiceControl";
import { InvoiceMailLieferantOverview } from "@/pages/invoiceMail/InvoiceMailLieferantOverview";
import {
  listInvoiceApprovalApprovers,
  type InvoiceApprovalApprover,
} from "@/lib/invoiceApproval";
import { fmtDateTime } from "@/pages/anfragen/anfrageWizardUtils";

type StatusFilter = InvoiceMailStatus | "all";

const POLL_INTERVAL_MS = 20_000;
const SCAN_POLL_INTERVAL_MS = 1500;

function scanJobIsActive(job: InvoiceMailScanJob | null): boolean {
  return job?.status === "queued" || job?.status === "running";
}

function statusBadge(status: InvoiceMailStatus): string {
  switch (status) {
    case "pending":
      return "bg-status-orange/15 text-status-orange";
    case "confirmed":
      return "bg-status-green/15 text-status-green";
    case "dismissed":
      return "bg-label-secondary/15 text-label-secondary";
    case "skipped":
      return "bg-label-secondary/10 text-label-secondary";
    default:
      return "bg-label-secondary/15 text-label-secondary";
  }
}

function statusLabel(status: InvoiceMailStatus): string {
  switch (status) {
    case "pending":
      return "Offen";
    case "confirmed":
      return "Abgelegt";
    case "dismissed":
      return "Verworfen";
    case "skipped":
      return "Ohne Anhang";
    default:
      return status;
  }
}

function senderLabel(row: InvoiceMailCandidate): string {
  const name = (row.from_name || "").trim();
  const email = (row.from_email || "").trim();
  if (name && email) return `${name} <${email}>`;
  return name || email || "—";
}

function isNoAttachmentRow(row: InvoiceMailCandidate): boolean {
  return row.status === "skipped";
}

export function InvoiceMailIndexPage() {
  const [items, setItems] = useState<InvoiceMailCandidate[]>([]);
  const [mailbox, setMailbox] = useState("invoice@ernsthaus.de");
  const [defaultSubfolder, setDefaultSubfolder] = useState("03 - Lieferanten/Eingangsrechnungen");
  const [liveMonitor, setLiveMonitor] = useState(true);
  const [counts, setCounts] = useState({ total: 0, with_attachments: 0, without_attachments: 0 });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanSinceDate, setScanSinceDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [scanJob, setScanJob] = useState<InvoiceMailScanJob | null>(null);
  const scanDoneNotifiedRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmCandidate, setConfirmCandidate] = useState<InvoiceMailCandidate | null>(null);
  const [approvers, setApprovers] = useState<InvoiceApprovalApprover[]>([]);
  const [dismissingId, setDismissingId] = useState<number | null>(null);
  const [reanalyzingId, setReanalyzingId] = useState<number | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await listInvoiceMailCandidates(
        statusFilter === "all" ? undefined : statusFilter,
      );
      setItems(res.items || []);
      setMailbox(res.mailbox || "invoice@ernsthaus.de");
      setDefaultSubfolder(res.default_subfolder || "03 - Lieferanten/Eingangsrechnungen");
      setLiveMonitor(res.live_monitor ?? true);
      setCounts(
        res.counts ?? {
          total: res.items?.length ?? 0,
          with_attachments: (res.items || []).filter((i) => i.has_attachments).length,
          without_attachments: (res.items || []).filter((i) => !i.has_attachments).length,
        },
      );
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Laden fehlgeschlagen.";
      if (!silent) setError(msg);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void listInvoiceApprovalApprovers()
      .then((res) => setApprovers(res.items || []))
      .catch(() => setApprovers([]));
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      // Versteckter Tab: kein Hintergrund-Polling (Serverlast).
      if (document.visibilityState === "hidden") return;
      void load(true);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const pendingCount = useMemo(
    () => items.filter((i) => i.status === "pending").length,
    [items],
  );

  useEffect(() => {
    const stored = sessionStorage.getItem(INVOICE_MAIL_SCAN_JOB_STORAGE_KEY);
    if (!stored) return;
    void getInvoiceMailScanJob(stored)
      .then((job) => {
        if (scanJobIsActive(job)) setScanJob(job);
        else sessionStorage.removeItem(INVOICE_MAIL_SCAN_JOB_STORAGE_KEY);
      })
      .catch(() => sessionStorage.removeItem(INVOICE_MAIL_SCAN_JOB_STORAGE_KEY));
  }, []);

  useEffect(() => {
    if (!scanJob?.job_id || !scanJobIsActive(scanJob)) return;
    const jobId = scanJob.job_id;

    const poll = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void getInvoiceMailScanJob(jobId)
        .then((job) => {
          setScanJob(job);
          if (job.status === "completed" && scanDoneNotifiedRef.current !== jobId) {
            scanDoneNotifiedRef.current = jobId;
            toast.success(job.message || "Mail-Analyse abgeschlossen.");
            sessionStorage.removeItem(INVOICE_MAIL_SCAN_JOB_STORAGE_KEY);
            void load(true);
          } else if (job.status === "failed" && scanDoneNotifiedRef.current !== jobId) {
            scanDoneNotifiedRef.current = jobId;
            toast.error(job.error || job.message || "Mail-Analyse fehlgeschlagen.");
            sessionStorage.removeItem(INVOICE_MAIL_SCAN_JOB_STORAGE_KEY);
          }
        })
        .catch(() => {
          sessionStorage.removeItem(INVOICE_MAIL_SCAN_JOB_STORAGE_KEY);
        });
    }, SCAN_POLL_INTERVAL_MS);

    return () => window.clearInterval(poll);
  }, [scanJob?.job_id, scanJob?.status, load]);

  const scanInProgress = scanJobIsActive(scanJob);

  async function handleScan() {
    if (!scanSinceDate) {
      toast.error("Bitte ein Startdatum wählen.");
      return;
    }
    if (scanInProgress) return;
    setScanning(true);
    try {
      const res = await scanInvoiceMailbox({ sinceDate: scanSinceDate });
      if (!res.job_id) {
        toast.success(res.message || "Analyse gestartet.");
        return;
      }
      sessionStorage.setItem(INVOICE_MAIL_SCAN_JOB_STORAGE_KEY, res.job_id);
      scanDoneNotifiedRef.current = null;
      const job = await getInvoiceMailScanJob(res.job_id);
      setScanJob(job);
      toast.success(res.message || `Analyse ab ${scanSinceDate} gestartet.`);
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Scan fehlgeschlagen.";
      toast.error(msg);
    } finally {
      setScanning(false);
    }
  }

  async function handleReanalyze(id: number) {
    setReanalyzingId(id);
    try {
      await reanalyzeInvoiceMailCandidate(id);
      toast.success("Mail erneut analysiert.");
      await load(true);
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Analyse fehlgeschlagen.";
      toast.error(msg);
    } finally {
      setReanalyzingId(null);
    }
  }

  async function handleDismiss(id: number) {
    if (!window.confirm("Diese Mail wirklich verwerfen (keine Ablage)?")) return;
    setDismissingId(id);
    try {
      await dismissInvoiceMailCandidate(id);
      toast.success("Vorschlag verworfen.");
      await load();
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Verwerfen fehlgeschlagen.";
      toast.error(msg);
    } finally {
      setDismissingId(null);
    }
  }

  function openMail(row: InvoiceMailCandidate) {
    setConfirmCandidate(row);
    setConfirmOpen(true);
  }

  function handleCandidateUpdated(updated: InvoiceMailCandidate) {
    setItems((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
    setConfirmCandidate(updated);
  }

  function handleAssigned(candidateId: number, assigneeLogin: string, assigneeName: string, status: string) {
    setItems((prev) =>
      prev.map((row) =>
        row.id === candidateId
          ? {
              ...row,
              approval: {
                status,
                assignee_name: assigneeName,
                assignee_login: assigneeLogin,
              },
            }
          : row,
      ),
    );
  }

  return (
    <AppShell
      title="Rechnungs-Postfach"
      subtitle={`${mailbox} – Live-Überwachung${liveMonitor ? " aktiv" : ""}, manuelle Freigabe`}
    >
      <div className="space-y-4">
        <Alert variant="info">
          Neue Mails werden automatisch analysiert (Microsoft-Graph-Webhook).
          Für ältere Mails ein Startdatum wählen und „Mails ab Datum analysieren“ — es werden
          alle eingegangenen Mails ab diesem Tag verarbeitet (Bestellnummern + E-Rechnung).
          Die SharePoint-Ablage erfolgt erst nach manueller Freigabe. Für die sachliche
          Prüfung weisen Sie die Rechnung einem Büro-Mitarbeiter zu – der Vorschlag
          folgt dem Kürzel der TK-Nummer.
        </Alert>

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-label-secondary">Mails analysieren ab</span>
            <input
              type="date"
              className="rounded border border-separator bg-card px-3 py-1.5 text-sm"
              value={scanSinceDate}
              onChange={(e) => setScanSinceDate(e.target.value)}
            />
          </label>
          <Button
            onClick={() => void handleScan()}
            disabled={scanning || loading || !scanSinceDate || scanInProgress}
          >
            {scanning || scanInProgress ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              "Mails ab Datum analysieren"
            )}
          </Button>
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            Aktualisieren
          </Button>
          <Link
            to="/bestellwesen/eingangsrechnung-artikel"
            className="inline-flex items-center rounded-md border border-separator px-3 py-1.5 text-sm text-label-primary hover:bg-card"
          >
            Eingangsrechnung-Artikel
          </Link>
          <span className="text-sm text-label-secondary">
            <span className="mr-3">
              Mit Anhang: <strong>{counts.with_attachments}</strong>
            </span>
            <span>
              Ohne Anhang: <strong>{counts.without_attachments}</strong>
            </span>
          </span>
          <span className="text-sm text-label-secondary">
            Zielordner: <code>{defaultSubfolder}/</code>
          </span>
        </div>

        {scanJob ? (
          <Card className="px-4 py-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-label-primary">Mail-Analyse</div>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                  scanJob.status === "completed"
                    ? "bg-status-green/15 text-status-green"
                    : scanJob.status === "failed"
                      ? "bg-status-red/15 text-status-red"
                      : "bg-accent/15 text-accent"
                }`}
              >
                {scanJob.status === "queued"
                  ? "Warteschlange"
                  : scanJob.status === "running"
                    ? scanJob.phase === "collecting"
                      ? "Zählen…"
                      : "Analysieren…"
                    : scanJob.status === "completed"
                      ? "Fertig"
                      : "Fehler"}
              </span>
            </div>
            <p className="mb-3 text-sm text-label-secondary">
              {scanJob.message || "Analyse läuft…"}
            </p>
            <div className="mb-3 h-2 overflow-hidden rounded-full bg-label-secondary/15">
              {scanJob.phase === "collecting" || scanJob.total == null ? (
                <div className="h-full w-1/3 animate-pulse rounded-full bg-accent" />
              ) : (
                <div
                  className="h-full rounded-full bg-accent transition-all duration-300"
                  style={{
                    width: `${Math.min(100, Math.max(0, scanJob.progress_percent ?? 0))}%`,
                  }}
                />
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-label-secondary">
              {scanJob.total != null ? (
                <span>
                  Fortschritt: <strong>{scanJob.processed ?? 0}</strong> / {scanJob.total}
                </span>
              ) : (
                <span>
                  Verarbeitet: <strong>{scanJob.processed ?? 0}</strong>
                </span>
              )}
              {(scanJob.matched ?? 0) > 0 ? (
                <span>
                  TK-Treffer: <strong>{scanJob.matched}</strong>
                </span>
              ) : null}
              {(scanJob.einvoice_parsed ?? 0) > 0 ? (
                <span>
                  E-Rechnungen: <strong>{scanJob.einvoice_parsed}</strong>
                  {(scanJob.einvoice_lines ?? 0) > 0 ? ` (${scanJob.einvoice_lines} Pos.)` : ""}
                </span>
              ) : null}
              {(scanJob.errors ?? 0) > 0 ? (
                <span className="text-status-red">
                  Fehler: <strong>{scanJob.errors}</strong>
                </span>
              ) : null}
              {scanJob.truncated ? (
                <span className="text-status-orange">Limit erreicht</span>
              ) : null}
            </div>
            {(scanJob.status === "completed" || scanJob.status === "failed") && !scanInProgress ? (
              <div className="mt-3">
                <Button size="sm" variant="secondary" onClick={() => setScanJob(null)}>
                  Ausblenden
                </Button>
              </div>
            ) : null}
          </Card>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Link
            to="/bestellwesen/rechnungsfreigabe"
            className="rounded-full border border-separator px-3 py-1 text-xs font-medium text-accent hover:bg-card"
          >
            Rechnungsfreigabe
          </Link>
          {(["all", "pending", "skipped", "confirmed", "dismissed"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                statusFilter === s
                  ? "bg-accent text-white"
                  : "border border-separator text-label-secondary hover:bg-card"
              }`}
            >
              {s === "all" ? "Alle" : statusLabel(s as InvoiceMailStatus)}
              {s === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}
            </button>
          ))}
        </div>

        {error ? <Alert variant="error">{error}</Alert> : null}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <p className="px-4 py-8 text-center text-sm text-label-secondary">
              Keine Mails gefunden. Neue Mails erscheinen automatisch oder nach „Postfach nachladen“.
            </p>
          </Card>
        ) : (
          <>
            <InvoiceMailLieferantOverview />

            <div className="space-y-2">
            {items.map((row) => {
              const noAttachment = isNoAttachmentRow(row);
              const einvoiceLines = einvoiceLineCountFromCandidate(row);
              const tkPreview = (row.matched_bestellnummern || []).slice(0, 3);
              const targetCount = (row.match_targets || []).length;
              const attCount = row.attachments_summary?.length || 0;

              return (
                <Card
                  key={row.id}
                  className={`overflow-hidden transition-colors hover:border-accent/30 ${noAttachment ? "opacity-50 grayscale" : ""}`}
                >
                  <button
                    type="button"
                    className="flex w-full flex-wrap items-start justify-between gap-3 px-4 py-3 text-left"
                    onClick={() => openMail(row)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-label-primary">
                        {row.subject || "(ohne Betreff)"}
                      </div>
                      <div className="text-sm text-label-secondary">{senderLabel(row)}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-label-secondary">
                        <span>{row.received_at ? fmtDateTime(row.received_at) : "—"}</span>
                        {attCount > 0 ? <span>{attCount} Anhang{attCount === 1 ? "" : "e"}</span> : null}
                        {targetCount > 0 ? (
                          <span>
                            {targetCount} Ablageziel{targetCount === 1 ? "" : "e"}
                          </span>
                        ) : (
                          <span className="text-status-orange">Keine Zuordnung</span>
                        )}
                      </div>
                      {tkPreview.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {tkPreview.map((tk) => (
                            <span
                              key={tk}
                              className="rounded bg-accent/10 px-2 py-0.5 font-mono text-xs text-accent"
                            >
                              {tk}
                            </span>
                          ))}
                          {(row.matched_bestellnummern?.length || 0) > tkPreview.length ? (
                            <span className="text-xs text-label-secondary">
                              +{(row.matched_bestellnummern?.length || 0) - tkPreview.length}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          row.has_attachments
                            ? "bg-accent/15 text-accent"
                            : "bg-label-secondary/15 text-label-secondary"
                        }`}
                      >
                        {row.has_attachments ? "Mit Anhang" : "Ohne Anhang"}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadge(row.status)}`}
                      >
                        {statusLabel(row.status)}
                      </span>
                      {einvoiceLines > 0 ? (
                        <span className="rounded-full bg-status-green/15 px-2 py-0.5 text-xs font-semibold text-status-green">
                          E-Rechnung · {einvoiceLines} Pos.
                        </span>
                      ) : row.has_attachments ? (
                        <span className="rounded-full bg-label-secondary/10 px-2 py-0.5 text-xs font-semibold text-label-secondary">
                          Keine E-Rechnung
                        </span>
                      ) : null}
                    </div>
                  </button>

                  <AssignInvoiceControl row={row} approvers={approvers} onAssigned={handleAssigned} />
                  <div className="flex flex-wrap gap-2 px-4 pb-2">
                    <Button size="sm" onClick={() => openMail(row)}>
                      {row.status === "pending" ? "Prüfen & ablegen" : "Ansehen"}
                    </Button>
                    {!noAttachment ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={reanalyzingId === row.id}
                        onClick={() => void handleReanalyze(row.id)}
                      >
                        {reanalyzingId === row.id ? (
                          <Spinner className="h-3.5 w-3.5" />
                        ) : (
                          "Erneut analysieren"
                        )}
                      </Button>
                    ) : null}
                    {row.status === "pending" ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={dismissingId === row.id}
                        onClick={() => void handleDismiss(row.id)}
                      >
                        {dismissingId === row.id ? (
                          <Spinner className="h-3.5 w-3.5" />
                        ) : (
                          "Verwerfen"
                        )}
                      </Button>
                    ) : null}
                  </div>
                </Card>
              );
            })}
            </div>
          </>
        )}
      </div>

      <ConfirmInvoiceMailDialog
        open={confirmOpen}
        candidate={confirmCandidate}
        defaultSubfolder={defaultSubfolder}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmCandidate(null);
        }}
        onConfirmed={() => void load()}
        onUpdated={handleCandidateUpdated}
      />
    </AppShell>
  );
}
